import React, { useState } from 'react';
import { 
  Home, 
  Calendar as CalendarIcon, 
  User as UserIcon, 
  CheckCircle,
  Clock,
  ChevronRight,
  CreditCard,
  MapPin,
  ChevronLeft,
  Star
} from 'lucide-react';
import { GlassCard, GlassButton, Avatar, ScreenContainer, Header } from '../components/GlassUI';
import { MOCK_MASTER, MOCK_SERVICES } from '../constants';
import { Service } from '../types';

interface ClientViewProps {
  onLogout: () => void;
  currentUser: any;
}

export const ClientView: React.FC<ClientViewProps> = ({ onLogout, currentUser }) => {
  const [activeTab, setActiveTab] = useState<'book' | 'history' | 'profile'>('book');

  // Booking State
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  const resetBooking = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedTime(null);
    setBookingComplete(false);
  };

  const handleBookingConfirm = () => {
    setIsProcessingPayment(true);
    setTimeout(() => {
        setIsProcessingPayment(false);
        setBookingComplete(true);
    }, 2000);
  };

  const renderContent = () => {
    if (activeTab === 'history') return <AppointmentsHistory />;
    if (activeTab === 'profile') return <ClientProfile user={currentUser} onLogout={onLogout} />;
    
    // Default: Booking Flow
    if (bookingComplete) {
        return (
            <div className="flex flex-col items-center justify-center h-[70vh] text-center animate-fade-in">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6 shadow-xl shadow-green-100">
                    <CheckCircle size={48} />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Запись подтверждена!</h2>
                <p className="text-slate-500 mb-8 max-w-xs">Вы успешно записались на «{selectedService?.name}» к мастеру {MOCK_MASTER.name}.</p>
                <GlassButton onClick={resetBooking} fullWidth>Записаться еще</GlassButton>
            </div>
        )
    }

    return (
        <div className="animate-fade-in pb-24">
             {step > 1 && (
                 <button onClick={() => setStep(step - 1)} className="mb-4 text-slate-500 flex items-center gap-1 text-sm font-medium hover:text-slate-800 transition-colors">
                     <ChevronLeft size={16} /> Назад
                 </button>
             )}
            
            {step === 1 && (
                <>
                    <Header title="Онлайн запись" subtitle="Выберите услугу" />
                    
                    {/* Master Info Snippet */}
                    <div className="flex items-center gap-4 mb-6 p-4 bg-white/40 rounded-2xl border border-white/50">
                        <Avatar src={MOCK_MASTER.avatar} alt={MOCK_MASTER.name} size="lg" />
                        <div>
                            <h3 className="font-bold text-slate-800">{MOCK_MASTER.name}</h3>
                            <div className="flex items-center gap-1 text-yellow-500 text-sm">
                                <Star size={14} fill="currentColor" />
                                <span className="text-slate-700 font-semibold">4.9</span>
                                <span className="text-slate-400">(124 отзыва)</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-4">
                        {MOCK_SERVICES.map(service => (
                            <GlassCard key={service.id} onClick={() => { setSelectedService(service); setStep(2); }} className="flex justify-between items-center group hover:bg-white/80 cursor-pointer">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${service.color}`}>
                                        <span className="font-bold text-lg">{service.name[0]}</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800">{service.name}</h3>
                                        <p className="text-xs text-slate-500">{service.durationMin} мин • {service.price} ₽</p>
                                    </div>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center text-slate-400 group-hover:text-violet-600 group-hover:bg-white group-hover:shadow-md transition-all">
                                    <ChevronRight size={16} />
                                </div>
                            </GlassCard>
                        ))}
                    </div>
                </>
            )}

            {step === 2 && (
                <>
                   <Header title="Время записи" subtitle="Свободные окна" />
                   <GlassCard className="mb-6">
                       <h3 className="font-bold text-slate-700 mb-4">Ноябрь 26, 2024</h3>
                       <div className="grid grid-cols-3 gap-3">
                           {['10:00', '11:30', '14:00', '15:30', '16:45', '18:00'].map(time => (
                               <button 
                                key={time}
                                onClick={() => setSelectedTime(time)}
                                className={`py-3 rounded-xl text-sm font-semibold transition-all ${selectedTime === time ? 'bg-violet-600 text-white shadow-lg shadow-violet-300/50 scale-105' : 'bg-white border border-slate-100 text-slate-600 hover:border-violet-200'}`}
                               >
                                   {time}
                               </button>
                           ))}
                       </div>
                   </GlassCard>
                   <GlassButton disabled={!selectedTime} onClick={() => setStep(3)} fullWidth>Продолжить</GlassButton>
                </>
            )}

            {step === 3 && selectedService && (
                <>
                    <Header title="Подтверждение" subtitle="Проверьте данные" />
                    <GlassCard className="mb-6 space-y-6">
                        <div className="flex items-center gap-4 border-b border-slate-100 pb-4">
                             <Avatar src={MOCK_MASTER.avatar} alt={MOCK_MASTER.name} size="lg" />
                             <div>
                                 <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Мастер</p>
                                 <h3 className="font-bold text-lg text-slate-800">{MOCK_MASTER.name}</h3>
                                 <div className="flex items-center gap-1 text-slate-500 text-xs mt-1">
                                     <MapPin size={12} />
                                     <span>Beauty Salon, Москва</span>
                                 </div>
                             </div>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Услуга</span>
                                <span className="font-semibold text-slate-800 text-right">{selectedService.name}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Дата и время</span>
                                <span className="font-semibold text-slate-800">26 Ноя, {selectedTime}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Длительность</span>
                                <span className="font-semibold text-slate-800">{selectedService.durationMin} мин</span>
                            </div>
                            <div className="pt-3 border-t border-slate-100 flex justify-between items-center">
                                <span className="text-slate-800 font-bold">Итого</span>
                                <span className="text-2xl font-bold text-violet-600">{selectedService.price} ₽</span>
                            </div>
                        </div>
                    </GlassCard>

                    <div className="space-y-3">
                        <GlassButton 
                            onClick={handleBookingConfirm} 
                            disabled={isProcessingPayment} 
                            fullWidth
                            className="bg-gradient-to-r from-violet-600 to-indigo-600"
                        >
                             {isProcessingPayment ? 'Обработка...' : `Оплатить онлайн (${selectedService.price} ₽)`}
                        </GlassButton>
                        <GlassButton 
                            variant="secondary" 
                            onClick={handleBookingConfirm} 
                            disabled={isProcessingPayment} 
                            fullWidth
                        >
                            Записаться (Оплата на месте)
                        </GlassButton>
                    </div>
                    <p className="text-center text-xs text-slate-400 mt-4">Нажимая кнопку, вы соглашаетесь с условиями сервиса</p>
                </>
            )}
        </div>
    );
  };

  return (
    <>
      <ScreenContainer>
        {renderContent()}
      </ScreenContainer>
      
      {!bookingComplete && (
          <div className="fixed bottom-6 left-4 right-4 max-w-md mx-auto z-50">
            <div className="bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-2xl flex justify-around items-center p-2 px-6">
                <NavButton icon={<Home size={24} />} active={activeTab === 'book'} onClick={() => { setActiveTab('book'); if(bookingComplete) resetBooking(); }} label="Запись" />
                <NavButton icon={<CalendarIcon size={24} />} active={activeTab === 'history'} onClick={() => setActiveTab('history')} label="Мои записи" />
                <NavButton icon={<UserIcon size={24} />} active={activeTab === 'profile'} onClick={() => setActiveTab('profile')} label="Профиль" />
            </div>
          </div>
      )}
    </>
  );
};

const NavButton: React.FC<{ icon: React.ReactNode; active: boolean; onClick: () => void, label: string }> = ({ icon, active, onClick, label }) => (
    <button 
      onClick={onClick}
      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-300 ${active ? 'text-violet-600' : 'text-slate-400 hover:text-slate-600'}`}
    >
      <div className={`${active ? 'bg-violet-100 p-2 rounded-xl' : ''}`}>{icon}</div>
      <span className="text-[10px] font-medium">{label}</span>
    </button>
);

const AppointmentsHistory: React.FC = () => {
    return (
        <div className="animate-fade-in pb-20">
            <Header title="Мои записи" />
            <div className="space-y-4">
                <GlassCard className="border-l-4 border-l-violet-500">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-slate-800">Стрижка и укладка</h3>
                        <span className="bg-violet-100 text-violet-700 text-xs font-bold px-2 py-1 rounded-lg">Скоро</span>
                    </div>
                    <p className="text-slate-500 text-sm mb-3">26 Ноя, 08:00 • 60 мин</p>
                    <div className="flex items-center gap-3">
                         <Avatar src={MOCK_MASTER.avatar} alt={MOCK_MASTER.name} size="sm" />
                         <span className="text-sm font-medium text-slate-700">{MOCK_MASTER.name}</span>
                    </div>
                </GlassCard>

                <GlassCard className="opacity-75 grayscale hover:grayscale-0 transition-all">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-slate-800">Маникюр комплекс</h3>
                        <span className="bg-slate-100 text-slate-500 text-xs font-bold px-2 py-1 rounded-lg">Завершен</span>
                    </div>
                    <p className="text-slate-500 text-sm mb-3">20 Окт, 10:00 • 90 мин</p>
                    <div className="flex items-center gap-3">
                         <Avatar src={MOCK_MASTER.avatar} alt={MOCK_MASTER.name} size="sm" />
                         <span className="text-sm font-medium text-slate-700">{MOCK_MASTER.name}</span>
                    </div>
                </GlassCard>
            </div>
        </div>
    )
}

const ClientProfile: React.FC<{ user: any, onLogout: () => void }> = ({ user, onLogout }) => {
    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex flex-col items-center py-6">
                <Avatar src={user.avatar} alt={user.name} size="xl" />
                <h2 className="text-2xl font-bold text-slate-800 mt-4">{user.name}</h2>
                <p className="text-slate-500">+7 999 123 45 67</p>
            </div>

            <GlassCard className="space-y-2">
                <h3 className="font-bold text-slate-800 mb-2">Способы оплаты</h3>
                <div className="flex items-center gap-3 p-3 bg-white/40 rounded-xl border border-white/50">
                    <div className="p-2 bg-slate-800 text-white rounded-lg">
                        <CreditCard size={20} />
                    </div>
                    <div className="flex-1">
                        <p className="font-bold text-sm text-slate-700">MasterCard **** 4242</p>
                        <p className="text-xs text-slate-500">Истекает 12/24</p>
                    </div>
                </div>
            </GlassCard>

            <GlassButton variant="secondary" onClick={onLogout} fullWidth className="text-red-500">
                Выйти из аккаунта
            </GlassButton>
        </div>
    )
}